import React from 'react';
import { Plane, Clock, ArrowRight, Luggage } from 'lucide-react';
import { resolveTripjackSegmentBaggage } from '../utils/tripjackTripBaggage';

const DesktopFlightDetails = ({
  flight,
  displayFlight,
  onwardSegments,
  returnSegments,
  isCombo = false,
  airlineLogos = {},
}) => {
  const fareDisplay = displayFlight || flight;
  // For combo flights (round trip), use onwardSegments and returnSegments
  // For regular flights, use flight.segments
  const segments = isCombo ? onwardSegments : flight?.segments;
  
  const renderSegments = (segmentList, title = null, iconRotate = false) => (
    <div className="space-y-4">
      {title && (
        <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
          <ArrowRight className={`w-4 h-4 text-teal-600 ${iconRotate ? 'rotate-180' : ''}`} />
          {title}
        </h4>
      )}
      {segmentList?.map((segment, idx) => {
        const departureTime = segment.dt?.split('T')[1]?.substring(0, 5) || 'N/A';
        const arrivalTime = segment.at?.split('T')[1]?.substring(0, 5) || 'N/A';
        const departureDate = segment.dt?.split('T')[0] || '';
        const arrivalDate = segment.at?.split('T')[0] || '';
        const segmentDuration = segment.duration || 0;
        const segmentHours = Math.floor(segmentDuration / 60);
        const segmentMinutes = segmentDuration % 60;
        const nextSegment = segmentList[idx + 1];
        
        return (
          <div key={segment.id || idx}>
            {/* Compact Segment Card */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 md:p-4">
              {/* Airline Info - Mobile: Full Width, Desktop: Inline */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
                {/* Airline Info */}
                <div className="flex items-center gap-3 md:min-w-[180px]">
                  <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
                    {airlineLogos[segment.fD?.aI?.code]?.square ? (
                      <img
                        src={airlineLogos[segment.fD?.aI?.code].square}
                        alt={segment.fD?.aI?.name || 'Airline'}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Plane className="w-4 h-4 text-teal-600" />
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-gray-900 leading-tight">
                      {segment.fD?.aI?.name || 'Airline'}
                    </span>
                    <span className="text-xs text-gray-500">{segment.fD?.fN || 'N/A'}</span>
                  </div>
                </div>

                {/* Route Timeline - Responsive */}
                <div className="flex items-center flex-1 gap-2 md:gap-3">
                  {/* Departure */}
                  <div className="text-left md:text-right">
                    <div className="text-base md:text-lg font-bold text-gray-900 leading-tight">{departureTime}</div>
                    <div className="text-xs text-gray-600 font-medium">
                      {segment.da?.code || 'XXX'}
                    </div>
                    {departureDate && (
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        {new Date(departureDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </div>
                    )}
                  </div>

                  {/* Flight Path */}
                  <div className="flex items-center gap-1 md:gap-2 px-1 md:px-2 flex-1">
                    <div className="w-2 h-2 rounded-full bg-teal-500 flex-shrink-0"></div>
                    <div className="h-px flex-1 bg-teal-300 min-w-[40px] md:min-w-[80px] relative">
                      <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 md:w-4 md:h-4 text-teal-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
                      </svg>
                    </div>
                    <div className="w-2 h-2 rounded-full bg-teal-300 flex-shrink-0"></div>
                  </div>

                  {/* Arrival */}
                  <div className="text-right">
                    <div className="text-base md:text-lg font-bold text-gray-900 leading-tight">{arrivalTime}</div>
                    <div className="text-xs text-gray-600 font-medium">
                      {segment.aa?.code || 'XXX'}
                    </div>
                    {arrivalDate && (
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        {new Date(arrivalDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Duration Badge - Mobile: Full Width, Desktop: Inline */}
                <div className="flex items-center justify-center md:justify-start gap-2 bg-white border border-teal-200 px-3 py-1.5 rounded-full w-full md:w-auto">
                  <Clock className="w-3.5 h-3.5 text-teal-600" />
                  <span className="text-xs font-bold text-teal-700 whitespace-nowrap">
                    {segmentHours}h {segmentMinutes}m
                  </span>
                </div>
              </div>

              {/* Secondary Info Row - City Names & Terminals */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mt-3 pt-3 border-t border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-4 text-xs text-gray-500">
                  <span>
                    <span className="font-medium text-gray-700">{segment.da?.city || 'City'}</span>
                    {segment.da?.terminal && <span className="ml-1">Terminal {segment.da.terminal}</span>}
                  </span>
                  <span className="hidden md:inline text-gray-300">→</span>
                  <span>
                    <span className="font-medium text-gray-700">{segment.aa?.city || 'City'}</span>
                    {segment.aa?.terminal && <span className="ml-1">Terminal {segment.aa.terminal}</span>}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  <span className="font-medium">{segment.fD?.eT || 'Economy'}</span>
                </div>
              </div>

              {(() => {
                const { checked, cabin } = resolveTripjackSegmentBaggage(segment, fareDisplay, flight);
                if (!checked && !cabin) return null;
                return (
                  <div className="flex items-start gap-2 mt-2 pt-2 border-t border-gray-200 text-xs text-gray-700">
                    <Luggage className="w-3.5 h-3.5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-gray-800">Baggage</span>
                      {cabin && <p className="mt-0.5">Cabin: {cabin}</p>}
                      {checked && <p className="text-gray-600">Checked: {checked}</p>}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Layover Info - Compact */}
            {nextSegment && segment.cT && (
              <div className="flex items-center justify-center py-2">
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                  <Clock className="w-3.5 h-3.5 text-amber-600" />
                  <span className="text-xs font-bold text-amber-700">
                    Layover: {Math.floor(segment.cT / 60)}h {segment.cT % 60}m
                  </span>
                  <span className="text-xs text-amber-600">in {segment.aa?.city || 'Transit'}</span>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6">
      {isCombo ? (
        <>
          {/* Onward Journey */}
          {renderSegments(onwardSegments, `Onward: ${flight?.from} → ${flight?.to}`, false)}
          
          {/* Return Journey */}
          {renderSegments(returnSegments, `Return: ${flight?.to} → ${flight?.from}`, true)}
        </>
      ) : (
        renderSegments(segments)
      )}

      {/* Trip Summary - Responsive Grid */}
      {!isCombo && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 pt-3 border-t border-gray-200">
          <div className="bg-white rounded-lg border border-gray-200 p-2.5">
            <p className="text-xs text-gray-500 mb-0.5 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Total Duration
            </p>
            <p className="text-sm font-bold text-gray-900">{flight.duration}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-2.5">
            <p className="text-xs text-gray-500 mb-0.5">Stops</p>
            <p className="text-sm font-bold text-gray-900">
              {flight.segments?.length === 1 ? 'Non-Stop' : `${flight.segments?.length - 1} Stop${flight.segments?.length > 2 ? 's' : ''}`}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-2.5">
            <p className="text-xs text-gray-500 mb-0.5 flex items-center gap-1">
              <Luggage className="w-3 h-3" />
              Baggage
            </p>
            <p className="text-sm font-bold text-gray-900">
              {fareDisplay?.tripBaggageBySegmentId && segments?.length > 1
                ? 'Varies by segment (see above)'
                : [fareDisplay?.cabinBaggage, fareDisplay?.baggage].filter(Boolean).join(' · ') || '—'}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-2.5">
            <p className="text-xs text-gray-500 mb-0.5">Operated By</p>
            <p className="text-sm font-bold text-gray-900">{flight.segments?.[0]?.fD?.aI?.name || 'N/A'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopFlightDetails;
